import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

interface BreadcrumbContextValue {
  trail: BreadcrumbItem[];
  publish: (items: BreadcrumbItem[] | null) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null);

/**
 * Spec 229 — the trail lives in the bar, not in the page.
 *
 * `Breadcrumbs` used to render its own trail inside 21 different pages. The
 * Kridt bar owns that trail, so the component now PUBLISHES here and renders
 * nothing. Keeping the component's public API identical is what makes this a
 * one-file change instead of a 21-file one.
 */
export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [trail, setTrail] = useState<BreadcrumbItem[]>([]);
  const publish = useCallback((items: BreadcrumbItem[] | null) => {
    setTrail(items ?? []);
  }, []);
  const value = useMemo(() => ({ trail, publish }), [trail, publish]);
  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
}

export function useBreadcrumbTrail(): BreadcrumbItem[] {
  return useContext(BreadcrumbContext)?.trail ?? [];
}

/**
 * Publish a trail for as long as the calling component is mounted, and clear it
 * on unmount so a page that declares no crumbs never inherits its predecessor's.
 *
 * `items` is serialised for the dependency comparison because every caller
 * builds a fresh array literal on each render; depending on the array identity
 * would republish on every render and loop.
 */
export function usePublishBreadcrumbs(items: BreadcrumbItem[]): void {
  const ctx = useContext(BreadcrumbContext);
  const publish = ctx?.publish;
  const key = JSON.stringify(items);
  useEffect(() => {
    if (!publish) return;
    publish(JSON.parse(key) as BreadcrumbItem[]);
    return () => publish(null);
  }, [publish, key]);
}
